<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-dialog flex items-center justify-center bg-theme-text/60 px-4 py-6"
      data-test="insufficient-tokens-modal"
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-md rounded-theme-md border border-theme-border bg-theme-surface-elevated shadow-theme-dialog">
        <div class="flex items-start gap-3 border-b border-theme-border px-5 py-4">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-theme-warning/10 text-theme-warning">
            <icon :path="mdiWallet" size="20" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-base font-semibold text-theme-text">
              {{ t('tokens.insufficient.title') }}
            </h2>
            <p class="mt-1 text-sm text-theme-text-secondary">
              {{ message }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-full p-1 text-theme-text-muted transition hover:bg-theme-surface-raised hover:text-theme-text"
            :aria-label="t('common.close')"
            @click="close"
          >
            <icon :path="mdiClose" size="18" />
          </button>
        </div>

        <div class="space-y-3 px-5 py-4">
          <div class="rounded-theme-sm bg-theme-surface-raised p-3 text-sm">
            <div class="flex justify-between gap-4">
              <span class="text-theme-text-muted">{{ t('tokens.operation') }}</span>
              <span class="text-right font-medium text-theme-text" data-test="insufficient-tokens-operation">
                {{ operationLabel }}
              </span>
            </div>
            <div v-if="requiredTokens !== null" class="mt-2 flex justify-between gap-4">
              <span class="text-theme-text-muted">{{ t('tokens.required') }}</span>
              <span class="font-medium text-theme-text">{{ requiredTokens }}</span>
            </div>
            <div v-if="balance !== null" class="mt-2 flex justify-between gap-4">
              <span class="text-theme-text-muted">{{ t('tokens.available') }}</span>
              <span class="font-medium text-theme-text">{{ balance }}</span>
            </div>
          </div>

          <p class="text-sm text-theme-text-secondary">
            {{ t('tokens.insufficient.body') }}
          </p>
        </div>

        <div class="flex flex-col-reverse gap-2 border-t border-theme-border px-5 py-4 sm:flex-row sm:justify-end">
          <AppButton
            variant="ghost"
            tone="neutral"
            size="md"
            @click="close"
          >
            {{ t('common.cancel') }}
          </AppButton>
          <AppButton
            data-test="insufficient-tokens-topup"
            variant="solid"
            tone="primary"
            size="md"
            @click="requestTopUp"
          >
            {{ t('tokens.top_up') }}
          </AppButton>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { tokenOperationLabel } from '@/config/tokenOperations';
  import { useTokenBalanceStore } from '@/stores/useTokenBalanceStore';
  import { mdiClose, mdiWallet } from '@mdi/js';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const tokenStore = useTokenBalanceStore();

  const visible = ref(false);
  const detail = ref({});

  const requiredTokens = computed(() => detail.value.requiredTokens ?? null);
  const balance = computed(() => detail.value.balance ?? tokenStore.account?.total_balance ?? null);
  const operationLabel = computed(() => tokenOperationLabel(detail.value.operationKey, t));
  const message = computed(() => {
    if (requiredTokens.value !== null && balance.value !== null) {
      return t('tokens.insufficient.balance_message', {
        balance: balance.value,
        required: requiredTokens.value,
      });
    }

    if (requiredTokens.value !== null) {
      return t('tokens.insufficient.required_message', {
        required: requiredTokens.value,
      });
    }

    return t('tokens.insufficient.default_message');
  });

  function open(event) {
    detail.value = event?.detail || {};
    visible.value = true;
    tokenStore.refresh({ force: true }).catch(() => {});
  }

  function close() {
    visible.value = false;
  }

  function requestTopUp() {
    window.dispatchEvent(
      new CustomEvent('tokens:top-up', {
        detail: {
          balance: balance.value,
          purchasedBalance: tokenStore.purchasedBalance,
          requiredTokens: requiredTokens.value,
          operationKey: detail.value.operationKey,
        },
      })
    );
    close();
  }

  onMounted(() => {
    window.addEventListener('tokens:insufficient', open);
    window.addEventListener('tokens:top-up:completed', close);
  });

  onUnmounted(() => {
    window.removeEventListener('tokens:insufficient', open);
    window.removeEventListener('tokens:top-up:completed', close);
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.18s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
